<div class="product-dialog-container">
  <!-- Fixed Header with Step Indicator and Error Messages -->
  <div class="fixed-header">
    <!-- Dialog Title Section -->
    <div class="dialog-title-section">
      <div class="flex justify-between items-start">
        <div>
          <h1 class="text-xl font-medium text-surface-on">
            {{ data.duplicate ? 'Duplicate From "' + (data.product?.name?.english || 'Product') + '"' : (data.product ?
            'Edit Product' : 'Add New Product') }}
          </h1>
          <p class="text-sm mt-2 text-surface-on">Enter the required data and save</p>
        </div>
        <button type="button" class="close-button" (click)="closeDialog()" aria-label="Close dialog">
          <mat-icon svgIcon="close" class="w-6 h-6"></mat-icon>
        </button>
      </div>
    </div>

    <!-- Step Indicator -->
    <div class="step-indicator-fixed">
      <div class="flex items-center justify-between">
        @for (step of steps(); track step.key; let i = $index) {
        <div class="flex items-center" [class.flex-1]="i < steps().length - 1">
          <div
            class="step-circle flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium cursor-pointer transition-colors"
            [class.step-active]="i === currentStep()" [class.step-completed]="i < currentStep()"
            [class.step-pending]="i > currentStep()" (click)="goToStep(i)">
            @if (i < currentStep()) { <mat-icon svgIcon="check" class="w-4 h-4"></mat-icon>
              } @else {
              {{ i + 1 }}
              }
          </div>
          <span class="ml-2 text-sm font-medium" [class.text-primary]="i === currentStep()"
            [class.text-gray-600]="i !== currentStep()">
            {{ step.label }}
          </span>
          @if (i < steps().length - 1) { <div class="flex-1 h-px bg-gray-300 mx-4">
        </div>
        }
      </div>
      }
    </div>
  </div>

  <!-- Fixed Error Messages -->
  <div class="error-messages-fixed">
    @if (submitError()) {
    <div class="error-message text-error px-4 py-3 text-sm bg-error-50 border border-error-200 rounded-lg shadow-sm">
      <div class="flex items-center gap-2">
        <mat-icon svgIcon="alert-circle" class="w-4 h-4 flex-shrink-0"></mat-icon>
        <span>{{ submitError() }}</span>
      </div>
    </div>
    }

    @if (stepValidationErrors().length > 0) {
    <div class="error-message text-error px-4 py-3 text-sm bg-error-50 border border-error-200 rounded-lg shadow-sm">
      <div class="flex items-start gap-2">
        <mat-icon svgIcon="alert-circle" class="w-4 h-4 flex-shrink-0 mt-0.5"></mat-icon>
        <div class="space-y-1">
          @for (error of stepValidationErrors(); track error) {
          <div>{{ error }}</div>
          }
        </div>
      </div>
    </div>
    }
  </div>
</div>

<!-- Scrollable Content Area with Separate Containers -->
<div class="scrollable-content" #scrollContainer>
  <cac-dialog-layout [title]="''" [subtitle]="''" [hideControls]="false" [submitBtn]="canSave() ? 'Save' : 'Next'"
    (onSubmit)="onMainAction($event)">
    <!-- Step Content -->
    <div class="step-content-wrapper">

      <!-- Step: Information -->
      @if (steps()[currentStep()].key === 'information') {
      <div class="step-section">
        <h2 class="text-xl font-semibold mb-6 text-gray-900">Product Information</h2>

        <!-- Share Link Display -->
        @if (shouldShowShareLink()) {
        @let errorMessage = getShareLinkMessage();
        <div class="mb-6 p-4 border rounded-lg"
          [class]="errorMessage ? 'text-orange-700 bg-orange-50 border-orange-200' : 'text-green-700 bg-green-50 border-green-200'">
          <div>
            <div class="min-w-0">
              <div class="text-sm font-medium mb-2">
                Product Share Link
                @if (errorMessage) {
                <span
                  class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  Inactive
                </span>
                } @else {
                <span
                  class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Active
                </span>
                }
              </div>
              <div class="text-sm break-all font-mono bg-white px-3 py-2 rounded border">
                <a [href]="getShareLinkForDisplay()" target="_blank" rel="noopener noreferrer"
                  class="text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                  {{ getShareLinkForDisplay() }}
                </a>
              </div>
              <div class="text-xs mt-2 space-y-1">
                <div>
                  @if (errorMessage) {
                  {{ errorMessage }}
                  } @else {
                  This link is active because the product is approved and enabled.
                  }
                </div>
                <div class="text-gray-500 italic">
                  Note: Save the product to see any changes reflected in the link.
                </div>
              </div>
            </div>
          </div>
        </div>
        }

        <div class="border rounded-lg shadow-sm border-gray-200 p-6 mb-6">
          <cac-form-builder [options]="basicInfo"></cac-form-builder>
          @if (data.isAdmin && basicInfo.controls.isPreOrder.value) {
            <label class="flex flex-col gap-1 text-sm mt-4 max-w-sm">Availability date (optional)
              <input type="date" [formControl]="preOrderAvailableOn" class="h-10 border rounded px-2 bg-transparent" />
            </label>
          }

          @if (isBundle()) {
          <section class="pt-6 border-t border-gray-200 mt-6" aria-labelledby="bundle-configuration-heading">
            <div class="flex flex-wrap items-end justify-between gap-4 mb-5">
              <div>
                <h3 id="bundle-configuration-heading" class="text-lg font-medium">Bundle Configuration</h3>
                <p class="text-sm text-gray-500 mt-1">Choose the products included in one unit of this bundle.</p>
              </div>
              <div class="w-full sm:w-72">
                <label for="bundle-stock-mode" class="block text-sm font-medium text-gray-700 mb-2">Stock source</label>
                <select id="bundle-stock-mode" class="w-full p-2.5 border border-gray-300 rounded-md bg-white"
                  (change)="setBundleStockMode(+$any($event.target).value)">
                  <option value="0" [selected]="isBundleStockModeSelected(0)">Manual bundle stock</option>
                  <option value="1" [selected]="isBundleStockModeSelected(1)">Calculated from components</option>
                </select>
              </div>
            </div>

            @if (isDerivedBundle()) {
            <div class="flex items-center justify-between border-y border-gray-200 py-3 mb-5">
              <span class="text-sm font-medium text-gray-700">Available bundle stock</span>
              <span class="text-sm font-semibold text-gray-900">
                @if (calculatedBundleStock() === undefined) {
                Waiting for component stock
                } @else if (calculatedBundleStock() === null) {
                Unlimited
                } @else {
                {{ calculatedBundleStock() }}
                }
              </span>
            </div>
            }

            <app-lazy-dropdown [multiple]="false" [searchable]="true" [clearable]="true"
              [clearOnSelection]="true" [placeholder]="'Search for a component product...'" [pageSize]="20"
              [apiAdapter]="bundleComponentApiAdapter" [itemMapper]="productItemMapper"
              [showCustomTemplate]="true"
              [excludeIds]="getBundleExcludedProductIds()" idsFilterKey="ProductIds" searchField="EnglishName"
              (selectionChange)="onBundleProductChange($event)" class="w-full" />

            <div class="mt-5 border-t border-gray-200">
              @if (bundleComponents().length > 0) {
              <div class="flex items-center justify-between py-3 border-b border-gray-200">
                <span class="text-sm font-medium text-gray-700">Component price total</span>
                <span class="text-sm font-semibold text-gray-900">{{ bundleComponentPriceTotal() }} KWD</span>
              </div>
              }
              @for (component of bundleComponents(); let i = $index; track component.componentVariantId) {
              <div class="grid grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_7rem_8rem_2.5rem] gap-3 items-end py-4 border-b border-gray-200">
                <div class="min-w-0">
                  <span class="block text-xs font-medium text-gray-500 mb-1">Product</span>
                  <div class="flex items-center gap-3 min-w-0">
                    <div class="w-12 h-12 flex-shrink-0 border border-gray-200 rounded-md overflow-hidden bg-gray-50">
                      @if (component.imageUrl) {
                      <img [src]="component.imageUrl" [alt]="component.productName"
                        class="w-full h-full object-cover" loading="lazy" />
                      } @else {
                      <div class="w-full h-full flex items-center justify-center text-xs text-gray-400">No image</div>
                      }
                    </div>
                    <span class="block text-sm font-medium text-gray-900 truncate">{{ component.productName }}</span>
                  </div>
                </div>
                <div>
                  <label [for]="'bundle-variant-' + i" class="block text-xs font-medium text-gray-500 mb-1">Variant</label>
                  <select [id]="'bundle-variant-' + i" class="w-full p-2 border border-gray-300 rounded-md bg-white"
                    [value]="component.componentVariantId"
                    (change)="updateBundleComponentVariant(i, +$any($event.target).value)">
                    @for (variant of component.variants; track variant.id) {
                    <option [value]="variant.id">{{ bundleVariantLabel(variant) }}</option>
                    }
                  </select>
                </div>
                <div>
                  <label [for]="'bundle-quantity-' + i" class="block text-xs font-medium text-gray-500 mb-1">Quantity</label>
                  <input [id]="'bundle-quantity-' + i" type="number" min="1" step="1"
                    class="w-full p-2 border border-gray-300 rounded-md"
                    [value]="component.quantity"
                    (input)="updateBundleComponentQuantity(i, +$any($event.target).value)" />
                </div>
                <div>
                  <label [for]="'bundle-unit-price-' + i" class="block text-xs font-medium text-gray-500 mb-1">Unit Price (KWD)</label>
                  <input [id]="'bundle-unit-price-' + i" type="number" step="0.01" required
                    class="w-full p-2 border border-gray-300 rounded-md"
                    [value]="component.unitPrice"
                    (input)="updateBundleComponentUnitPrice(i, +$any($event.target).value)" />
                </div>
                <button type="button" class="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-red-600"
                  title="Remove component" (click)="removeBundleComponent(i)">
                  <cac-icon icon="trash" size="1.125rem"></cac-icon>
                </button>
              </div>
              }

              @if (bundleComponents().length === 0) {
              <p class="py-6 text-sm text-center text-gray-500">No bundle components selected.</p>
              }
            </div>
          </section>
          }

          <!-- descriptions moved to Content step -->
          @if (!isVariable() && variations().length > 0) {
          <div class="pt-4 border-t border-gray-200 mt-6">
            <h3 class="text-lg font-medium mb-4">{{ isBundle() ? 'Bundle Details' : 'Variation Details' }}</h3>
            <lib-variation-form [variation]="variations()[0]" [showActions]="false"
              [stockReadOnly]="isDerivedBundle()" [stockReadOnlyValue]="calculatedBundleStock()"
              [regularPriceLabel]="isBundle() ? 'Bundle Price (KWD)' : 'Regular Price (KWD)'" />
          </div>
          }
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-900">Primary Category</h3>
            <!-- Selected Primary Category Display -->
            @if (categoriesFormGroup.controls.primaryCategory.value) {
            <div class="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div class="text-sm font-medium text-blue-800 mb-1">Selected Category:</div>
              <div class="text-blue-700">{{ getCategoryName(categoriesFormGroup.controls.primaryCategory.value) | async
                }}</div>
            </div>
            }
            <div class="border rounded-3xl p-4 bg-gray-50/50 h-[500px] flex flex-col">
              <app-category-tree [categories]="categories$" [control]="categoriesFormGroup.controls.primaryCategory"
                [allowParentSelection]="false" [isSearchable]="true" class="flex-1 min-h-0" />
            </div>
          </div>

          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-900">Secondary Categories</h3>
            <!-- Selected Secondary Categories Display -->
            @if (categoriesFormGroup.controls.secondaryCategories.value &&
            categoriesFormGroup.controls.secondaryCategories.value.length > 0) {
            <div class="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div class="text-sm font-medium text-emerald-800 mb-2">Selected Categories:</div>
              @for (categoryName of (getCategoryNames(categoriesFormGroup.controls.secondaryCategories.value) | async);
              track categoryName) {
              <div class="inline-block bg-emerald-100 text-emerald-800 px-2 py-1 rounded text-sm mr-2 mb-1">
                {{ categoryName }}
              </div>
              }
            </div>
            }
            <div class="border rounded-3xl p-4 bg-gray-50/50 h-[500px] flex flex-col">
              <app-category-tree [categories]="categories$" [control]="categoriesFormGroup.controls.secondaryCategories"
                [multiple]="true" [allowParentSelection]="false" [isSearchable]="true" class="flex-1 min-h-0" />
            </div>
          </div>
        </div>
      </div>
      }

      <!-- Step: Media -->
      @if (steps()[currentStep()].key === 'media') {
      <div class="step-section">
        <h2 class="text-xl font-semibold mb-6 text-gray-900 border-b border-gray-200 pb-4">Content</h2>

        <!-- Content: Product Descriptions -->
        <div class="border rounded-3xl p-6 bg-gray-50/50 mb-6">
          <div class="space-y-4">
            <div>
              <div class="mb-2 font-medium">English Description</div>
              <quill-editor dir="ltr" (onEditorCreated)="onEnglishEditorCreated($event)"
                [formControl]="englishDescriptionControl" theme="snow">
              </quill-editor>
            </div>

            <div>
              <div class="mb-2 font-medium">Arabic Description</div>
              <quill-editor class="arabic-editor" (onEditorCreated)="onArabicEditorCreated($event)"
                [formControl]="arabicDescriptionControl" theme="snow">
              </quill-editor>
            </div>
          </div>
        </div>

        <!-- Images Section -->
        <div class="mb-8">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <cac-icon icon="camera" size="1.5rem" iconClass="text-blue-600"></cac-icon>
              Images
            </h3>
            <cac-button (click)="addImage()" icon="plus" theme="primary" appearance="stroked" size="sm">
              Add Image
            </cac-button>
          </div>

          <div class="border rounded-3xl p-6 bg-gray-50/30 min-h-32">
            @if (productImages$.value.length > 0) {
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              @for (image of productImages$.value; track image.path; let i = $index) {
              <div
                class="group relative bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
                <!-- Image Preview -->
                <div class="aspect-square relative overflow-hidden bg-gray-100">
                  <img [src]="image.url" [alt]="'Product image ' + (i + 1)"
                    class="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                    loading="lazy" />
                  <!-- Order Badge -->
                  <div
                    class="absolute top-2 left-2 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
                    #{{ image.order }}
                  </div>
                  <!-- Actions Overlay -->
                  <div
                    class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2">
                    <a [href]="image.url" target="_blank" rel="noopener noreferrer"
                      class="bg-white/90 hover:bg-white text-gray-700 w-8 h-8 rounded-full transition-colors flex items-center justify-center"
                      title="Open in New Tab">
                      <cac-icon icon="external-link" size="0.875rem"
                        wrapperClass="flex items-center justify-center"></cac-icon>
                    </a>
                    <button (click)="downloadImage(image.url)"
                      class="bg-white/90 hover:bg-white text-gray-700 w-8 h-8 rounded-full transition-colors flex items-center justify-center"
                      title="Download Image">
                      <cac-icon icon="download" size="0.875rem"
                        wrapperClass="flex items-center justify-center"></cac-icon>
                    </button>
                    <button (click)="editImageOrder(i)"
                      class="bg-white/90 hover:bg-white text-gray-700 w-8 h-8 rounded-full transition-colors flex items-center justify-center"
                      title="Edit Order">
                      <cac-icon icon="edit" size="0.875rem" wrapperClass="flex items-center justify-center"></cac-icon>
                    </button>
                    <button (click)="removeImage(i)"
                      class="bg-red-500/90 hover:bg-red-600 text-white w-8 h-8 rounded-full transition-colors flex items-center justify-center"
                      title="Remove Image">
                      <cac-icon icon="trash" size="0.875rem" iconClass="text-white"
                        wrapperClass="flex items-center justify-center"></cac-icon>
                    </button>
                  </div>
                </div>
              </div>
              }
            </div>
            } @else {
            <div class="text-center py-12">
              <cac-icon icon="camera" size="4rem" iconClass="text-gray-400 mb-4"></cac-icon>
              <p class="text-gray-500 mb-4">No images added yet</p>
              <cac-button (click)="addImage()" icon="plus" theme="primary">
                Add Your First Image
              </cac-button>
            </div>
            }
          </div>
        </div>

        <!-- Videos Section -->
        <div>
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <cac-icon icon="play" size="1.5rem" iconClass="text-purple-600"></cac-icon>
              Videos
            </h3>
            <cac-button (click)="addVideo()" icon="plus" theme="primary" appearance="stroked" size="sm">
              Add Video
            </cac-button>
          </div>

          <div class="border rounded-3xl p-6 bg-gray-50/30 min-h-32">
            @if (productVideos$.value.length > 0) {
            <div class="space-y-3">
              @for (video of productVideos$.value; track video.url; let i = $index) {
              <div
                class="group bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md transition-all duration-200">
                <div class="flex items-center gap-4">
                  <!-- Video Icon -->
                  <div class="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <cac-icon icon="play" size="1.5rem" iconClass="text-purple-600"></cac-icon>
                  </div>

                  <!-- Video Info -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <span class="text-sm font-medium text-gray-900">Video {{ i + 1 }}</span>
                      <span class="bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-1 rounded-full">
                        Order #{{ video.order }}
                      </span>
                    </div>
                    <p class="text-sm text-gray-600 truncate" [title]="video.url">{{ video.url }}</p>
                  </div>

                  <!-- Actions -->
                  <div
                    class="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button (click)="editVideoOrder(i)"
                      class="text-gray-500 hover:text-blue-600 w-8 h-8 rounded-full transition-colors flex items-center justify-center"
                      title="Edit Order">
                      <cac-icon icon="edit" size="1.125rem"></cac-icon>
                    </button>
                    <button (click)="removeVideo(i)"
                      class="text-gray-500 hover:text-red-600 w-8 h-8 rounded-full transition-colors flex items-center justify-center"
                      title="Remove Video">
                      <cac-icon icon="trash" size="1.125rem"></cac-icon>
                    </button>
                  </div>
                </div>
              </div>
              }
            </div>
            } @else {
            <div class="text-center py-12">
              <cac-icon icon="play" size="4rem" iconClass="text-gray-400 mb-4"></cac-icon>
              <p class="text-gray-500 mb-4">No videos added yet</p>
              <cac-button (click)="addVideo()" icon="plus" theme="primary">
                Add Your First Video
              </cac-button>
            </div>
            }
          </div>
        </div>
      </div>
      }

      <!-- Step: Specification -->
      @if (steps()[currentStep()].key === 'specification') {
      <div class="step-section">
        <h2 class="text-xl font-semibold mb-6 text-gray-900">Product Specifications</h2>

        <div class="border rounded-3xl p-6 bg-gray-50/50">
          <div class="space-y-6">
            @for (field of specifications(); let i = $index; track field) {
            <div class="flex flex-wrap items-center gap-4 p-4 bg-white rounded-2xl border border-gray-200">
              <cac-field [control]="field.controls.attributeId" label="Attribute" [items]="attributeItems$"
                controlType="select" class="flex-1 min-w-64" />
              <cac-field [control]="field.controls.termId" label="Attribute Value" [items]="getTermItems(field)"
                controlType="select" class="flex-1" />
              @if (data.isAdmin) {
              <cac-button (onClick)="addNewAttributeValue(field.controls.attributeId.value, $event)"
                appearance="stroked" icon="plus" iconSize="1.25rem" padding="0.5rem"
                [disabled]="!field.controls.attributeId.value" title="Add New Value" />
              }""
              <cac-button (click)="removeSpecification(i)" theme="error" appearance="text" icon="trash"
                iconSize="1.25rem" padding="0.5rem" class="delete-button"></cac-button>
            </div>
            }

            @if (specifications().length === 0) {
            <div class="text-center py-8 text-gray-500">
              <p>No specifications added yet. Click the button below to add your first specification.</p>
            </div>
            }
          </div>

          <div class="mt-6 pt-4 border-t border-gray-200">
            <cac-button class="w-full sm:w-auto" (click)="createSpecificationForm()" icon="plus" theme="primary">
              Add Specification
            </cac-button>
          </div>
        </div>
      </div>
      }

      <!-- Step: Variants -->
      @if (steps()[currentStep()].key === 'variants') {
      <div class="step-section">
        <h2 class="text-xl font-semibold mb-6 text-gray-900">Product Variants</h2>

        <!-- Import from Simple Products Section -->
        <div *ngIf="this.data.isAdmin" class="border rounded-3xl p-6 bg-gray-50/50 mb-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Import from Simple Products</h3>

          <div class="mb-4">
            <app-lazy-dropdown [multiple]="false" [searchable]="true" [clearable]="true" [clearOnSelection]="true"
              [placeholder]="'Search and select simple products...'"
              [hint]="'Select simple products to import their variant data into this variable product.'" [pageSize]="20"
              [apiAdapter]="simpleProductApiAdapter" [itemMapper]="productItemMapper"
              [excludeIds]="getImportedProductIds()" idsFilterKey="ProductIds" searchField="EnglishName"
              (selectionChange)="onImportedProductChange($event)" class="w-full" />
          </div>

          <!-- Imported Products List -->
          <div class="border rounded-3xl p-6 bg-gray-50/30">
            @if (importedProducts().length > 0) {
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              @for (product of importedProducts(); track product.productId) {
              <div
                class="flex items-start gap-3 p-3 bg-white rounded-2xl border border-gray-200 hover:shadow-md transition-shadow">
                @if (product.detail) {
                <!-- Loaded product -->
                <img [src]="product.detail.images.length > 0 ? product.detail.images[0].url : '/assets/placeholder.png'"
                  alt="Product" class="w-16 h-16 object-cover rounded-lg flex-shrink-0" />
                <div class="flex-1 min-w-0">
                  <p class="font-medium text-gray-900 truncate">{{ product.detail.name.english }}</p>
                  @if (product.detail.name.arabic && product.detail.name.arabic !== product.detail.name.english) {
                  <p class="text-sm text-gray-600 truncate">{{ product.detail.name.arabic }}</p>
                  }
                </div>
                <cac-button (click)="removeImportedProduct(product.productId)" theme="error" appearance="text"
                  icon="trash" iconSize="1rem" padding="0.5rem"></cac-button>
                } @else {
                <!-- Loading state -->
                <div class="w-16 h-16 bg-gray-200 rounded-lg flex-shrink-0 animate-pulse"></div>
                <div class="flex-1 min-w-0 space-y-2">
                  <div class="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                  <div class="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
                </div>
                <div class="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
                }
              </div>
              }
            </div>
            } @else {
            <div class="text-center py-8 text-gray-500">
              <p>No products imported yet. Select simple products above to import their data.</p>
            </div>
            }
          </div>
        </div>

        <!-- Attributes Section -->
        <div class="border rounded-3xl p-6 bg-gray-50/50 mb-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Attributes</h3>

          <div class="space-y-4">
            @for (field of attributes(); let i = $index; track field) {
            <div class="flex flex-wrap items-center gap-4 p-4 bg-white rounded-2xl border border-gray-200">
              <cac-field [control]="field.controls.attributeId" label="Attribute" [items]="attributeItems$"
                controlType="select" class="flex-1 min-w-64" />
              <cac-field [control]="field.controls.termIds" label="Attribute Values" [items]="getTermItems(field)"
                controlType="select" [multiple]="true" class="flex-1" />
              @if (data.isAdmin) {
              <cac-button (onClick)="addNewAttributeValue(field.controls.attributeId.value, $event)"
                appearance="stroked" icon="plus" iconSize="1.25rem" padding="0.5rem"
                [disabled]="!field.controls.attributeId.value" title="Add New Value" />
              }
              <cac-button (click)="removeAttributeSet(i)" theme="error" appearance="text" icon="trash"
                iconSize="1.25rem" padding="0.5rem" class="delete-button" />
            </div>
            }

            @if (attributes().length === 0) {
            <div class="text-center py-8 text-gray-500">
              <p>No attributes added yet. Add attributes to create product variants.</p>
            </div>
            }
          </div>

          <div class="flex flex-wrap gap-4 mt-6 pt-4 border-t border-gray-200">
            <cac-button (click)="createAttributeForm()" icon="plus" theme="primary">
              Add Attribute
            </cac-button>
            <cac-button (click)="updateAllTermCombinations()" appearance="stroked"
              [disabled]="attributes().length === 0">
              Generate Variants
            </cac-button>
          </div>
        </div>

        <!-- Variants Section -->
        <div class="border rounded-3xl p-6 bg-gray-50/50">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Variants</h3>

          <div class="space-y-4">
            @for (variant of variations(); let i = $index; track variant) {
            @let controls = variant.form.controls;

            <div class="border rounded-2xl shadow-sm border-gray-200 bg-white">
              <button type="button"
                class="w-full text-left flex items-center justify-between p-4 hover:bg-neutral-100 transition-colors"
                (click)="toggleVariant(i)">
                <div class="flex items-center gap-3">
                  <cac-icon icon="chevron-down" size="1.25rem"
                    [iconClass]="'transition-transform duration-200 text-gray-500' + (expandedVariants[i] ? ' rotate-180' : '')"></cac-icon>
                  <div class="flex flex-wrap gap-2">
                    @for (attr of variant.attributes; track attr) {
                    <cac-chips [active]="true" class="text-black bg-primary-100">
                      {{ getTermName(attr) | async }}
                    </cac-chips>
                    }
                  </div>
                </div>
                <cac-switch [control]="controls.status" [label]="controls.status.getValue() ? 'Enabled':'Disabled'"
                  (click)="$event.stopPropagation()" />
              </button>

              <div class="transition-all duration-300 overflow-hidden"
                [style.max-height]="expandedVariants[i] ? '1400px' : '0px'">
                <div class="p-4 border-t border-gray-200">
                  <!-- Import Variant Data Dropdown -->
                  @if (importedProducts().length > 0) {
                  <div class="mb-4 p-4 bg-blue-50/50 rounded-2xl border border-blue-200">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Import variant data from product</label>
                    <select class="form-select w-full p-2 border border-gray-300 rounded-md"
                      [value]="getVariantImportedProductId(i) || ''"
                      (change)="onVariantImportSelection(i, +$any($event.target).value)">
                      <option value="">Select a product to import data...</option>
                      @for (item of getImportedProductItems(); track item.id) {
                      <option [value]="item.id">{{ item.label }}</option>
                      }
                    </select>
                  </div>
                  }

                  <lib-variation-form (delete)="deleteVariation(i)" [variation]="variant" />
                </div>
              </div>
            </div>
            }

            @if (variations().length === 0) {
            <div class="text-center py-8 text-gray-500">
              <p>No variants created yet. Add attributes and generate variants to get started.</p>
            </div>
            }
          </div>
        </div>
      </div>
      }

      <!-- Step: Related Products -->
      @if (steps()[currentStep()].key === 'related-products') {
      <div class="step-section">
        <!-- Product Selection Section -->
        <div class="border rounded-3xl p-6 bg-gray-50/50 mb-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-900">
              People also buy
            </h3>
          </div>

          <div class="mb-4">
            <app-lazy-dropdown [multiple]="false" [searchable]="true" [clearable]="true" [clearOnSelection]="true"
              [placeholder]="'Search and select products...'"
              [hint]="'Type to search for products. Selected items will be added to the list below.'" [pageSize]="20"
              [apiAdapter]="productApiAdapter" [itemMapper]="productItemMapper"
              [excludeIds]="getSelectedRelatedProductIds()" idsFilterKey="ProductIds" searchField="EnglishName"
              [duplicateMessage]="'Product is already in selected'" (selectionChange)="onRelatedProductsChange($event)"
              class="w-full" />
          </div>
        </div>

        <!-- Selected Related Products -->
        <div class="border rounded-3xl p-6 bg-gray-50/30">
          @if (relatedProducts$.value.length > 0) {
          <div class="space-y-3">
            @for (product of relatedProducts$.value; track product.id; let i = $index) {
            <div
              class="group bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md transition-all duration-200">
              <div class="flex items-center gap-4">
                <!-- Product Image -->
                <div class="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl overflow-hidden">
                  @if (product.thumbnail?.url) {
                  <img [src]="product.thumbnail?.url" [alt]="product.name.english" class="w-full h-full object-cover"
                    loading="lazy" />
                  } @else {
                  <div class="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span class="text-gray-400 text-xs">No Image</span>
                  </div>
                  }
                </div>

                <!-- Product Info -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <span class="text-sm font-medium text-gray-900">{{ product.name.english }}</span>
                    <span class="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full">
                      ID: {{ product.id }}
                    </span>
                  </div>
                  @if (product.name.arabic && product.name.arabic !== product.name.english) {
                  <p class="text-sm text-gray-600">{{ product.name.arabic }}</p>
                  }
                </div>

                <!-- Remove Action -->
                <div class="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button (click)="removeRelatedProduct(product.id)"
                    class="text-gray-500 hover:text-red-600 w-8 h-8 rounded-full transition-colors flex items-center justify-center"
                    title="Remove Product">
                    <cac-icon icon="trash" size="1.125rem"></cac-icon>
                  </button>
                </div>
              </div>
            </div>
            }
          </div>
          } @else {
          <div class="text-center py-12">
            <p class="text-gray-500 mb-4">No product selected yet</p>
            <p class="text-sm text-gray-400">Use the search dropdown above to find and select products</p>
          </div>
          }
        </div>
      </div>
      }

    </div>

    <!-- Previous Button in Bottom Slot -->
    <div class="navigation-controls flex items-center w-full" slot="bottom">
      <cac-button *ngIf="canGoToPreviousStep()" (click)="previousStep()" appearance="stroked" icon="chevron-left">
        Previous
      </cac-button>
      <cac-button *ngIf="!canSave()" (onClick)="onSubmit($event)" appearance="stroked" class="ml-auto">
        Save
      </cac-button>
    </div>
  </cac-dialog-layout>
</div>
</div>
